import { api } from "jiffy-api";
import { formatTimeAMPM } from "common/utils/format/date";

/**
 * Utility class for managing cached delivery dates functionality
 * Provides methods to check, apply, and update cached delivery dates
 */
export class CachedDeliveryManager {
  /**
   * Checks if cached delivery should be used based on container data attributes
   * @param {string} containerId - The ID of the container element to check
   * @returns {boolean}
   */
  static shouldUseCachedDelivery(containerId) {
    const $container = document.getElementById(containerId);
    if (!$container) return false;

    const hasCachedDelivery = $container.dataset.hasCachedDelivery === "true";
    return hasCachedDelivery && window.jiffyCachedDeliveryData?.deliveryDate;
  }

  /**
   * Updates the cached delivery date for the current zipcode
   * @returns {Promise<boolean>} - Returns true if successfully updated, false otherwise
   */
  static async updateCachedDeliveryDate() {
    if (!window.jiffyCachedDeliveryData) {
      window.jiffyCachedDeliveryData = {};
    }

    try {
      const response = await api.product.fetchCachedDeliveryDate();

      if (response.success && response.cached_delivery_date) {
        window.jiffyCachedDeliveryData.deliveryDate = response.cached_delivery_date;

        if (typeof response.has_delivery_start_end !== "undefined") {
          window.jiffyCachedDeliveryData.hasDeliveryStartEnd = response.has_delivery_start_end;
        }

        return true;
      }
    } catch (error) {
      // Fall through to clear cached data
    }

    window.jiffyCachedDeliveryData.deliveryDate = "";
    return false;
  }

  /**
   * Applies cached delivery date to delivery date elements
   * @param {NodeList|Array} $deliveryDateElements - Elements to update with cached delivery date
   * @param {Function} highlightCallback - Optional callback to highlight updated elements
   */
  static applyCachedDeliveryToElements($deliveryDateElements, highlightCallback) {
    if (
      !window.jiffyCachedDeliveryData?.deliveryDate ||
      typeof window.jiffyCachedDeliveryData?.deliveryDate !== "string" ||
      window.jiffyCachedDeliveryData?.deliveryDate.trim() === ""
    ) {
      return;
    }

    $deliveryDateElements.forEach($deliveryDateEl => {
      if ($deliveryDateEl) {
        $deliveryDateEl.textContent = window.jiffyCachedDeliveryData.deliveryDate;
        this.updateDeliveryStatusLabel($deliveryDateEl);
        if (highlightCallback) {
          highlightCallback($deliveryDateEl);
        }
      }
    });
  }

  /**
   * Determines if the current cached delivery is local (has time range)
   * @returns {boolean}
   */
  static isLocalDelivery() {
    return !!window.jiffyCachedDeliveryData?.hasDeliveryStartEnd;
  }

  /**
   * Updates the delivery label text based on local/standard delivery status
   * Respects regional delivery labels if set via data-regional-delivery-label
   * @param {Element} $element - The element containing the label selector
   */
  static updateDeliveryStatusLabel($element) {
    const labelSelector = $element?.dataset?.deliveryLabelSelector;
    const $label = labelSelector ? document.querySelector(labelSelector) : null;

    if ($label) {
      const regionalLabel = $element?.dataset?.regionalDeliveryLabel;
      const defaultLabel = this.isLocalDelivery() ? "Local Delivery" : "Standard Delivery";
      $label.textContent = regionalLabel || defaultLabel;
    }
  }

  /**
   * Hides cutoff sections when using cached delivery dates
   * @param {NodeList|Array} $cutoffElements - Cutoff elements whose parent containers should be hidden
   */
  static hideCutoffSections($cutoffElements) {
    $cutoffElements.forEach($cutoff => {
      if ($cutoff && $cutoff.parentElement) {
        $cutoff.parentElement.classList.add("hidden");
      }
    });
  }

  /**
   * Complete cached delivery application - updates elements and hides cutoffs
   * @param {Object} options - Configuration options
   * @param {NodeList|Array} options.deliveryDateElements - Elements to update with cached delivery date
   * @param {NodeList|Array} options.cutoffElements - Cutoff elements to hide
   * @param {Function} options.setLoadingState - Function to control loading state
   * @param {Function} options.highlightCallback - Optional callback to highlight updated elements
   */
  static applyCachedDelivery({
    deliveryDateElements,
    cutoffElements,
    setLoadingState,
    highlightCallback
  }) {
    if (
      !window.jiffyCachedDeliveryData?.deliveryDate ||
      typeof window.jiffyCachedDeliveryData?.deliveryDate !== "string" ||
      window.jiffyCachedDeliveryData?.deliveryDate.trim() === ""
    ) {
      return;
    }

    // Hide loading state
    if (setLoadingState) {
      setLoadingState(false);
    }

    // Update delivery date text
    this.applyCachedDeliveryToElements(deliveryDateElements, highlightCallback);

    // Hide cutoff sections when using cached delivery dates
    if (cutoffElements) {
      this.hideCutoffSections(cutoffElements);
    }
  }

  /* eslint-disable max-params */
  /**
   * Updates the time left text element with cutoff time or fallback message
   * @param {Element} $timeleftTextEl - The DOM element to update
   * @param {boolean} isTimeRangeAvailable - Whether time range is available
   * @param {string} cutOffTime - The cutoff time string
   * @param {boolean} [ceilHour=false] - Whether to round up hour when minutes > 0
   */
  static applyCachedTimeLeftText(
    $timeleftTextEl,
    isTimeRangeAvailable,
    cutOffTime,
    ceilHour = false
  ) {
    const noRushFees = "No sneaky rush fees";

    if (!$timeleftTextEl) {
      return;
    }

    if (isTimeRangeAvailable && cutOffTime) {
      try {
        $timeleftTextEl.textContent = `Order by ${formatTimeAMPM(cutOffTime, ceilHour)}`;
      } catch (error) {
        $timeleftTextEl.textContent = noRushFees;
      }
    } else {
      $timeleftTextEl.textContent = noRushFees;
    }
  }
  /* eslint-enable max-params */

  /**
   * Handles refresh or cached delivery functionality
   * @param {Object} options - Configuration options
   * @param {string} options.containerId - Container ID to check for cached delivery
   * @param {Function} options.applyCachedCallback - Function to call when applying cached delivery
   * @param {Function} options.fallbackCallback - Function to call when cached delivery is not available
   * @returns {Promise<void>}
   */
  static async handleRefreshDeliveryDates({ containerId, applyCachedCallback, fallbackCallback }) {
    // Try to update cached delivery date first
    if (this.shouldUseCachedDelivery(containerId)) {
      const updated = await this.updateCachedDeliveryDate();
      if (updated && applyCachedCallback) {
        applyCachedCallback();
        return;
      }
    }

    // Fall back to regular delivery estimation
    if (fallbackCallback) {
      fallbackCallback();
    }
  }
}

export default CachedDeliveryManager;
